import React, { useState } from "react";

import { PropTypes } from "prop-types";

import {
  Typography,
  Grid,
  Stack,
  Card,
  Tooltip,
  IconButton,
  Slider,
} from "@mui/material";
import LockOpenIcon from "@mui/icons-material/LockOpen";
import LockIcon from "@mui/icons-material/Lock";

import Icon from "../../../react_utils/Icon";
import Checkbox from "../../../react_utils/fields/Checkbox";
import InfoTooltip from "../../../react_utils/InfoTooltip";
import WallboxNameModal from "./WallboxNameModal";
import WallboxLockTimeModal from "./WallboxLockTimeModal";
import {
  wallboxLockedStateTimeSettings,
  wallboxLockHelp,
  wallboxState,
  wallboxLockedState,
  wallboxUnlockedState,
  surplusChargingTitle,
  surplusChargingHelp,
  hybridChargingTitle,
  hybridChargingHelp,
  hybridChargingCurrentHelp,
  hybridChargingCurrent,
} from "../../../shared/constants/wallbox";

const cardWidth = 300;
const cardHeight = 150;

export default function WallboxCard({
  wallbox,
  editName,
  setLocked,
  setSurplusCharging,
  setHybridCharging,
  addLockTime,
  deleteLockTime,
  infoText = true,
}) {
  const [hovered, setHovered] = useState(false);

  const wallboxSetPoint = wallbox._set_point;
  if (!wallboxSetPoint) return null;

  return (
    <Card
      key={`wallboxsetpoint-${wallbox.id}`}
      sx={{
        minWidth: cardWidth,
        minHeight: cardHeight,
        padding: "10px",
        backgroundColor: "tertiary.main",
      }}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      <Grid container justify="center">
        <Grid
          xs={6}
          sm={6}
          md={6}
          item
          style={{ display: "flex", alignItems: "center" }}
        >
          <Stack direction="row" spacing={1}>
            <Typography color="secondary" variant="h6">
              {editName ? wallbox.name : "Wallbox"}
            </Typography>
            {editName && (
              <WallboxNameModal
                wallbox={wallbox}
                editName={editName}
                modalProps={{
                  getOpenButton: (toggle) =>
                    hovered && (
                      <IconButton
                        sx={{ p: 0 }}
                        disableFocusRipple
                        disableRipple
                        style={{ backgroundColor: "transparent" }}
                        onClick={toggle}
                      >
                        <Icon
                          icon={"edit"}
                          sx={{ color: "secondary.main" }}
                          fontSize="small"
                        />
                      </IconButton>
                    ),
                }}
              />
            )}
          </Stack>
        </Grid>
        {wallbox.lockable && (
          <Grid
            xs={6}
            item
            style={{ display: "flex" }}
            justifyContent={"flex-end"}
          >
            <Stack direction="row" spacing={1}>
              <Tooltip
                title={
                  <Typography sx={{ fontSize: 14 }}>
                    {wallboxLockedStateTimeSettings}
                  </Typography>
                }
                enterTouchDelay={0}
              >
                {/* Following div is needed so that the tooltip is shown */}
                <div>
                  <WallboxLockTimeModal
                    wallbox={wallbox}
                    addLockTime={addLockTime}
                    deleteLockTime={deleteLockTime}
                    modalProps={{
                      getOpenButton: (toggle) => (
                        <IconButton
                          disableFocusRipple
                          disableRipple
                          style={{ backgroundColor: "transparent" }}
                          onClick={toggle}
                          disabled={!addLockTime || !deleteLockTime}
                        >
                          <Icon icon={"lockClock"} color="secondary" />
                        </IconButton>
                      ),
                    }}
                  />
                </div>
              </Tooltip>
              <Tooltip
                title={
                  <Typography sx={{ fontSize: 14 }}>
                    {wallboxSetPoint.locked ? "Entsperren" : "Sperren"}
                  </Typography>
                }
                enterTouchDelay={0}
              >
                {/* Following div is needed so that the tooltip is shown */}
                <div>
                  <Checkbox
                    checked={wallboxSetPoint.locked}
                    onChange={(value) => {
                      setLocked?.(value);
                    }}
                    label=""
                    disabled={!setLocked}
                    checkBoxProps={{
                      icon: <LockOpenIcon />,
                      checkedIcon: <LockIcon />,
                    }}
                  />
                </div>
              </Tooltip>
            </Stack>
          </Grid>
        )}
      </Grid>
      <Typography color="primary" variant="h6">
        {wallboxState}:{" "}
        {wallboxSetPoint.locked ? wallboxLockedState : wallboxUnlockedState}{" "}
        {infoText && wallbox.lockable && (
          <InfoTooltip>{wallboxLockHelp}</InfoTooltip>
        )}
      </Typography>
      <Stack direction="column" spacing={0}>
        {wallbox.surplus_charging_compatible && (
          <Checkbox
            checked={wallboxSetPoint.surplus_charging}
            onChange={(value) => {
              setSurplusCharging?.(value);
            }}
            disabled={!setSurplusCharging}
            label={
              <Typography>
                {surplusChargingTitle}{" "}
                {infoText && <InfoTooltip>{surplusChargingHelp}</InfoTooltip>}
              </Typography>
            }
          />
        )}
        {wallboxSetPoint.surplus_charging &&
          wallbox.hybrid_charging_compatible && (
            <Checkbox
              checked={wallboxSetPoint.hybrid_charging_current != null}
              onChange={(value) => {
                setHybridCharging?.(value ? 6 : null);
              }}
              disabled={!setHybridCharging}
              label={
                <Typography>
                  {hybridChargingTitle}{" "}
                  {infoText && <InfoTooltip>{hybridChargingHelp}</InfoTooltip>}
                </Typography>
              }
            />
          )}
        {wallboxSetPoint.hybrid_charging_current &&
          wallbox.hybrid_charging_compatible && (
            <>
              <Typography id="input-slider">
                {hybridChargingCurrent}:{" "}
                {wallboxSetPoint.hybrid_charging_current} A{" "}
                {infoText && (
                  <InfoTooltip>{hybridChargingCurrentHelp}</InfoTooltip>
                )}
              </Typography>
              <Slider
                aria-label="Ladestrom"
                defaultValue={6}
                getAriaValueText={(value) => `${value}`}
                valueLabelDisplay="auto"
                step={1}
                marks
                min={6}
                max={16}
                disabled={!setHybridCharging}
                value={wallboxSetPoint.hybrid_charging_current}
                sx={{ color: "secondary.main", width: 300 }}
                onChangeCommitted={(_, value) => setHybridCharging?.(value)}
              />
            </>
          )}
      </Stack>
    </Card>
  );
}

WallboxCard.propTypes = {
  wallbox: PropTypes.object,
  editName: PropTypes.func,
  setLocked: PropTypes.func,
  setSurplusCharging: PropTypes.func,
  setHybridCharging: PropTypes.func,
  addLockTime: PropTypes.func,
  deleteLockTime: PropTypes.func,
  infoText: PropTypes.bool,
};
